/**
 * Shared users context — single subscription to the users collection.
 * Replaces 13+ independent onSnapshot subscriptions across modules.
 *
 * Users are mapped with both `id` and `uid` fields for backward compatibility
 * since different modules use different property names.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useTenant } from './TenantContext';
import { userRoles } from '../lib/roles';
import { isSystemAccount } from '../lib/systemAccounts'; // RW-3407

const UsersContext = createContext(null);

export function UsersProvider({ children }) {
  const { tenantId, claimReady } = useTenant();
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);

  // RW-683: tenantId filter required by query-shape rule.
  // RW-1705: claimReady in the deps — a listener attached during the
  // claimless-token window dies with permission-denied (inTenant() checks the
  // CLAIM, not just the query shape) and the SDK never restarts it, leaving
  // every people search empty for the whole session. When the healed token
  // lands, claimReady flips and this re-subscribes.
  useEffect(() => {
    if (!tenantId) return undefined;
    const q = query(collection(db, 'users'), where('tenantId', '==', tenantId));
    const unsub = onSnapshot(q, (snap) => {
      setAllUsers(snap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() })));
      setError(null);
      setLoading(false);
    }, (err) => {
      // permission-denied is the expected claimless-window failure; anything
      // else deserves a trace. Either way, surface it — don't swallow.
      if (err?.code !== 'permission-denied') {
        console.warn('[UsersContext] users listener failed', err?.message);
      }
      setError(err || new Error('users listener failed'));
      setLoading(false);
    });
    return unsub;
  }, [tenantId, claimReady, retryTick]);

  // RW-1961: `error` above is only actionable if a consumer can ask for another
  // attempt. claimReady heals the RW-1705 claimless window on its own, but a
  // transient backend failure otherwise sits until a full page reload — so any
  // surface that renders a directory failure has no honest Try again to offer.
  // Clearing the error here (rather than waiting for the next snapshot) is what
  // lets those surfaces show a loading state on click instead of re-rendering
  // the same failure screen.
  const retry = useCallback(() => {
    setError(null);
    setLoading(true);
    setRetryTick((n) => n + 1);
  }, []);

  const value = useMemo(() => {
    const byId = {};
    allUsers.forEach((u) => { byId[u.id] = u; });
    // RW-3407: people only — a system account stays in usersById (its posts still name it)
    const active = allUsers.filter((u) => u.isActive !== false && u.displayName && !isSystemAccount(u));
    // The singular `role` is roles[0], unsorted — reading it alone dropped an
    // `[tc, agent]` agent from the agent pickers and an `[agent, admin]` admin
    // from the join-request fallback. Group by every role the doc holds.
    const agents = active.filter((u) => userRoles(u).includes('agent') || (!u.role && u.displayName));
    const brokers = active.filter((u) => userRoles(u).includes('broker') || u.role === 'manager');
    const admins = active.filter((u) => userRoles(u).includes('admin'));

    return {
      allUsers,
      activeUsers: active,
      agents,
      brokers,
      admins,
      usersById: byId,
      loading,
      error,
      totalUsers: allUsers.length,
      getUser: (id) => byId[id],
      retry,
    };
  }, [allUsers, loading, error, retry]);

  return <UsersContext.Provider value={value}>{children}</UsersContext.Provider>;
}

export function useSharedUsers() {
  const ctx = useContext(UsersContext);
  if (!ctx) throw new Error('useSharedUsers must be used within UsersProvider');
  return ctx;
}

// Provider-optional variant: null instead of a throw when no UsersProvider is
// above (detached React roots, e.g. the flipbook offscreen renderer). Lets
// callers degrade gracefully with one unconditional hook call.
export function useSharedUsersOptional() {
  return useContext(UsersContext);
}
